/**
 * 
 * @copyright     (c) 2025 Gist Applications Inc.
 * @author        Greg Olive greg@gist-apps.com
 * @package       gift-card-pro
 * 
 * /assets/js/React/Proxy/_utils/ComponentsUtils.js
 * Created:       Tue May 27 2025
 * Modified By:   Greg Olive
 * Last Modified: Tue Apr 28 2026
 */

import { getImage, md5, testLocalStorage } from "../../_utils";
import { getDefaultTimezoneValue } from "./DateUtils";
import { deepMerge } from "@root/_utils";


export const getStorageKey = (fulfillmentType) => {

  if (!fulfillmentType) {
    return null;
  }

  return (fulfillmentType === "physical") ? "gcp_form_data_physical" : "gcp_form_data";

};

export const getSavedData = (fulfillmentType) => {

  const localStorageKey = getStorageKey(fulfillmentType);

  if (!localStorageKey) {
    return {};
  }

  try {

    const isLocalDataSet = (testLocalStorage() === true && localStorage.getItem(localStorageKey) !== null);
    const localData = (isLocalDataSet) ? JSON.parse(localStorage.getItem(localStorageKey)) : {};
    
    return localData;

  } catch (error) {
    return {};
  }

}

export const setInitialImage = (localData, settings, fulfillmentType) => {

  
  // If fulfillment type is undefined, both are enabled, so default to digital.
  const ft = fulfillmentType || "digital";
    
  const selectedImages = settings[ft].greeting_card.selected_images;
  const categories     = settings[ft].greeting_card.categories

  const firstCategory  = (Array.isArray(selectedImages) && Array.isArray(categories))
    ? (categories.map((catName) => selectedImages.find((cat) => cat.name === catName && cat.images.length)).find(Boolean) || "")
    : "";

  if (!firstCategory) return "";

  const category       = firstCategory.name;
  const image          = firstCategory.images[0];

  // Build the correct path for the image
  const imageUrl       = getImage(image);
  const imageKey       = md5(`${image}0${category}`);
  const finalImage     = imageUrl + "?key=" + imageKey;
  const selectedImage  = getImage(finalImage);

  // Set default selected image if not set
  if (typeof(localData?.properties?._gcp_selected_card_image) !== 'undefined') {

    if (selectedImages.find((cat) => cat?.images?.includes(localData.properties._gcp_selected_card_image))) {
      return localData.properties._gcp_selected_card_image;
    }
    
  }

  return selectedImage;
  

}

const setInitialDate = (initialData) => {

  const dateObject   = new Date();
  dateObject.setSeconds(0);

  return (initialData.properties._gcp_delivery_date !== "") ? initialData.properties._gcp_delivery_date : dateObject.toISOString().split(".")[0].split(":").slice(0, -1).join(":");

}


/**
 * Load up the initial form data to populate the gift card form
 *
 * @param {Object} formData
 * @param {Array} initialProducts
 * @param {Object} settings
 * @return {Object}
 */
export const getInitialFormData = (formData, initialProducts, settings, inDashboard, fulfillmentType) => {

  const localData      = getSavedData(fulfillmentType);
  const initialData    = { ...formData};
  const fulfillmentKey = fulfillmentType === "physical" ? "Physical" : "Digital";
  const firstProduct   = fulfillmentType ? (initialProducts.find((prod) => prod.option1 === fulfillmentKey) || initialProducts[0]) : null;
  
  if (localData && localData.id) {
    initialData.id = localData.id;
    localData.id = localData.id;
  } else if (firstProduct) {
    initialData.id = firstProduct.id;
  }

  localData.properties = localData.properties || {};
  localData.fields     = localData.fields || {};
  localData.properties._gcp_selected_card_image = setInitialImage(localData, settings, fulfillmentType)
  localData.properties._gcp_delivery_date       = setInitialDate(initialData);
  localData.properties._gcp_delivery_timezone   = initialData.properties._gcp_delivery_timezone != "" ? initialData.properties._gcp_delivery_timezone : getDefaultTimezoneValue();;
  localData.properties._gcp_fulfillment_type    = localData.properties._gcp_fulfillment_type || undefined;
  
  return deepMerge(initialData, localData);

};

/**
 * Get the gift card amount.
 *
 * @param {Object} settings
 * @param {Object} formData
 */
export const getAmount = (settings, formData) => {

  let amount = 0.00;

  let selectedVariants = settings.product && settings.product.variants ? settings.product.variants.filter(variant => (variant.id + "") === (formData.id + "")) : [];

  if (formData.id === "Custom") {

    // Get the amount from the custom amount property.
    amount = typeof(formData.properties._gcp_custom_value) != 'undefined' ? formData.properties._gcp_custom_value : amount;

  } else if (typeof(selectedVariants[0]) != 'undefined' && typeof(selectedVariants[0].price) != 'undefined') {

    // Get the price of the selected variant
    amount =  selectedVariants[0].price.replace(/[^0-9\.-]+/g,"");

  }

  return amount;

};


/**
 * Build the initial list of gift card varians, and optionally, a custom value.
 *
 * @param {Object} settings
 */
export const getInitialVariants = (settings, fulfillmentType, preview = false) => {

  if (!settings || !settings.product) {
    return [];
  }

  settings.product = settings.product || {};
  
  // Handle preview mode, where products have yet to be created.
  if (preview === true && settings && settings.product) {

    settings.product.variants = settings[fulfillmentType].giftcard.preset_prices.map((value, index) => {
      return {
        id: index+1,
        price: value,
        title: value
      }
    });

  }

  // Sort by price
  settings.product.variants =  settings.product && settings.product.variants ? settings.product.variants.sort(function(a, b) {
    return Number(a.price) - Number(b.price);
  }) : [];

  return settings.product.variants;

};

